import { useSWR } from '@tiger/swr';
import { Table } from 'antd';
import { useMemo } from 'react';
import { ColumnProps } from 'antd/es/table';
import dayjs from 'dayjs';
import EmptyData from 'components/EmptyData';
import ErrorBox from 'components/ErrorBox';
import security from 'stores/security';
import API from 'utils/API';
import { formatMoney } from 'utils/format';
import StatusTag from '../StatusTag';
import './style.scss';

type ProcessStatus = 'PENDING' | 'FAILED' | 'SUCCESS';

interface RecordTypes {
    id: string;
    created_at: number;
    type: 'DEPOSIT' | 'WITHDRAW';
    method: 'ACH' | 'WIRE';
    currency: string;
    amount: number;
    process_status: ProcessStatus;
}

const processMap: Record<
    ProcessStatus,
    {
        process: string;
        desc: string;
    }
> = {
    PENDING: {
        process: 'pending',
        desc: __('Pending')
    },
    FAILED: {
        process: 'fail',
        desc: __('Failed')
    },
    SUCCESS: {
        process: 'success',
        desc: __('Success')
    }
};

const Auditing: React.FC = () => {
    const { isLoading, error, data, mutate } = useSWR<{
        items: RecordTypes[];
    }>(
        {
            url: API.TRADE.funding.getHistory(),
            params: {
                customer_id: security.currentCustomerId
            }
        },
        {
            dataChain: res => res.data
        }
    );

    const column: Array<ColumnProps<RecordTypes>> = useMemo(
        () => [
            {
                title: __('Date'),
                dataIndex: 'created_at',
                render: value => (value ? dayjs(value).format('YYYY/MM/DD HH:mm:ss') : '--'),
                width: '30%',
                className: 'funding-records-table-cell'
            },
            {
                title: __('Type'),
                dataIndex: 'type',
                width: '16%',
                render: value => (value === 'DEPOSIT' ? __('Deposit') : __('Withdraw')),
                className: 'funding-records-table-cell'
            },
            {
                title: __('Amount'),
                dataIndex: 'amount',
                render: (value, record) => (
                    <>
                        {formatMoney(Number(value).toFixed(2))} {record.currency}
                    </>
                ),
                width: '23%',
                align: 'right',
                className: 'funding-records-table-cell'
            },
            {
                title: __('Status'),
                dataIndex: 'process_status',
                render: value => <StatusTag custom={processMap[value] || { process: 'pending', desc: value }} />,
                align: 'right',
                className: 'funding-records-table-cell'
            }
        ],
        []
    );

    const render = () => {
        if (error && !data) {
            return <ErrorBox error={error} onClick={() => mutate()} />;
        }

        if (!data?.items?.length && !isLoading) {
            return <EmptyData />;
        }

        return (
            <Table
                className="funding-records-table"
                tableLayout="fixed"
                rowKey="id"
                loading={isLoading}
                dataSource={data?.items || []}
                columns={column}
                bordered={false}
                scroll={{ x: 500 }}
                pagination={false}
            />
        );
    };

    return <div className="funding-processing-records-root">{render()}</div>;
};

export default Auditing;
